import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import logoImage from '../assets/logo.png';

const PageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #36226f;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const Logo = styled.img`
  height: 50px;
  cursor: pointer;
  
  @media (max-width: 480px) {
    height: 40px;
  }
`;

const BackButton = styled.button`
  background-color: #381f74;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #2a1656;
  }
  
  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 14px;
  }
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 30px 15px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #381f74;
  margin-bottom: 10px;
  
  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const EffectiveDate = styled.p`
  font-weight: 600;
  color: #666;
  margin-bottom: 30px;
  font-size: 16px;
`;

const Section = styled.section`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: #381f74;
  margin-bottom: 15px;
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #333;
  margin-bottom: 15px;
  
  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const List = styled.ul`
  margin: 15px 0;
  padding-left: 20px;
`;

const ListItem = styled.li`
  font-size: 16px;
  line-height: 1.6;
  color: #333;
  margin-bottom: 8px;
  
  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const StyledLink = styled.a`
  color: #381f74;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Strong = styled.strong`
  font-weight: 600;
  color: #381f74;
`;

const ContactInfo = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
`;

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Privacy Policy</title>
        <meta name="description" content="Privacy Policy for Store Credit App. Learn how we collect, use, and protect your personal information." />
        <meta name="keywords" content="privacy policy, data protection, store credit app, personal information" />
      </Helmet>
      
      <PageContainer>
        <Header>
          <Logo src={logoImage} alt="Logo" onClick={handleLogoClick} />
          {/* <BackButton onClick={handleBackClick}>
            Back
          </BackButton> */}
        </Header>
        
        <ContentContainer>
          <Title>Privacy Policy</Title>
          <EffectiveDate><Strong>Effective Date:</Strong> June 10, 2025</EffectiveDate>

          <Paragraph>
            At <Strong>{APP_NAME}</Strong>, we value your privacy and are committed to protecting your personal information. 
            This Privacy Policy explains what information we collect, how we use it, and the choices you have.
          </Paragraph>

          <Section>
            <SectionTitle>1. Information We Collect</SectionTitle>
            <Paragraph>When you register an account with {APP_NAME}, we collect the following personal information:</Paragraph>
            <List>
              <ListItem>First name</ListItem>
              <ListItem>Last name</ListItem>
              <ListItem>Email address</ListItem>
              <ListItem>Mobile phone number</ListItem>
              <ListItem>Password (encrypted)</ListItem>
              <ListItem>Profile photo (used to help participating merchants identify you during in-person redemptions)</ListItem>
            </List>
            <Paragraph>We collect only the information necessary to provide our service and improve the user experience.</Paragraph>
          </Section>

          <Section>
            <SectionTitle>2. How We Use Your Information</SectionTitle>
            <Paragraph>We use your information to:</Paragraph>
            <List>
              <ListItem>Create and manage your account</ListItem>
              <ListItem>Enable in-store credit tracking and redemption</ListItem>
              <ListItem>Help merchants identify users during transactions</ListItem>
              <ListItem>Communicate with you about updates and promotions</ListItem>
              <ListItem>Improve our app and user experience</ListItem>
              <ListItem>Prevent fraud or abuse of our system</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>3. Email Communications</SectionTitle>
            <Paragraph>We may send you promotional emails to:</Paragraph>
            <List>
              <ListItem>Inform you of new participating merchants in your area</ListItem>
              <ListItem>Notify you of special promotions or higher-than-usual credit offers</ListItem>
              <ListItem>Share app updates or important news</ListItem>
            </List>
            <Paragraph>
              You can <Strong>unsubscribe</Strong> at any time by clicking the unsubscribe link in any email or by 
              contacting us at <StyledLink href="mailto:storecreditapp@gmail.com">storecreditapp@gmail.com</StyledLink>.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>4. Advertising</SectionTitle>
            <Paragraph>
              {APP_NAME} is a free service. To help support the platform, we may display in-app advertising and send 
              email promotions. We do not allow third-party advertising networks to collect your data through our app.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>5. Sharing of Information</SectionTitle>
            <Paragraph>We <Strong>do not sell, rent, or share</Strong> your personal information with third parties.</Paragraph>
            <Paragraph>
              However, if {APP_NAME} is ever sold or acquired, user data may be transferred to the new owner as part of 
              the business transaction. The new owner will be required to honour this Privacy Policy.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>6. Data Retention and Deletion</SectionTitle>
            <Paragraph>
              You may request to <Strong>delete your account</Strong> at any time by emailing{' '}
              <StyledLink href="mailto:storecreditapp@gmail.com">storecreditapp@gmail.com</StyledLink>. Upon deletion, 
              your data will be permanently removed, except where required by law.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>7. Security</SectionTitle>
            <Paragraph>
              We take reasonable precautions to protect your information from loss, misuse, and unauthorized access. 
              Your password is encrypted, and access to user data is limited to authorized personnel only.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>8. Children's Privacy</SectionTitle>
            <Paragraph>
              {APP_NAME} is not intended for use by individuals under the age of 13. We do not knowingly collect 
              personal data from children.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>9. Contact Us</SectionTitle>
            <Paragraph>If you have any questions about this policy or your data, please contact us:</Paragraph>
            <ContactInfo>
              <Paragraph style={{ marginBottom: '5px' }}>
                <Strong>{APP_NAME} Support</Strong>
              </Paragraph>
              <Paragraph style={{ marginBottom: '0' }}>
                📧 <StyledLink href="mailto:storecreditapp@gmail.com">storecreditapp@gmail.com</StyledLink>
              </Paragraph>
            </ContactInfo>
          </Section>
        </ContentContainer>
      </PageContainer>
    </>
  );
};

export default PrivacyPolicyPage;
